import { useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NativeOptionSheet,
  type NativeOption,
} from "@/components/native/NativeOptionSheet";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";

type SupportCaseBadgePickerProps = {
  value: string;
  options: NativeOption[];
  title: string;
  badgeClassName: string;
  disabled?: boolean;
  isSaving?: boolean;
  onChange: (value: string) => void;
};

export function SupportCaseBadgePicker({
  value,
  options,
  title,
  badgeClassName,
  disabled,
  isSaving,
  onChange,
}: SupportCaseBadgePickerProps) {
  const { isNative } = useNativePlatform();
  const [open, setOpen] = useState(false);
  const currentLabel = options.find((opt) => opt.value === value)?.label ?? value;
  const isDisabled = disabled || isSaving;

  const handleSelect = (next: string) => {
    if (next === value || isDisabled) return;
    onChange(next);
  };

  const badge = (
    <Badge
      className={cn(
        "gap-1 pr-1.5 select-none transition-opacity",
        badgeClassName,
        isDisabled ? "opacity-70" : "cursor-pointer hover:opacity-90",
      )}
    >
      <span>{currentLabel}</span>
      {isSaving ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <ChevronDown className="h-3 w-3 opacity-80" />
      )}
    </Badge>
  );

  if (isNative) {
    return (
      <>
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => setOpen(true)}
          className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={title}
        >
          {badge}
        </button>
        <NativeOptionSheet
          open={open}
          onOpenChange={setOpen}
          title={title}
          value={value}
          options={options}
          onSelect={handleSelect}
        />
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isDisabled}>
        <button
          type="button"
          className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={title}
        >
          {badge}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => handleSelect(opt.value)}
              className="flex items-center justify-between gap-3"
            >
              <span>{opt.label}</span>
              {selected ? (
                <Check className="h-4 w-4 text-accent" />
              ) : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
